import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, API_BASE } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import BrandLogo from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Mic, Shield, Eye, X, Command, PanelLeftOpen, PanelLeftClose, MessageSquare } from "lucide-react";
import Sidebar from "../components/chat/Sidebar";
import StoragePanel from "../components/chat/StoragePanel";
import ChatThread from "../components/chat/ChatThread";
import ChatInput from "../components/chat/ChatInput";
import VoiceCallModal from "../components/chat/VoiceCallModal";
import VoiceStartModal from "../components/chat/VoiceStartModal";
import VoiceTranscriptModal from "../components/chat/VoiceTranscriptModal";
import WorkspaceEmptyState from "../components/chat/WorkspaceEmptyState";
import CommandPalette from "../components/chat/CommandPalette";
import UserMenu from "../components/UserMenu";
import StrandedEmbeddingBanner from "../components/chat/StrandedEmbeddingBanner";

const STORAGE_OPEN_KEY = "nexa_storage_open_v2";
const SIDEBAR_OPEN_KEY = "faro_sidebar_left_open_v2";

export default function Workspace() {
  const { t } = useTranslation();
  const { convId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const branding = useBranding();
  const appName = branding.app_name || "Faro";

  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [models, setModels] = useState([]);
  const [selectedModelId, setSelectedModelId] = useState(null);
  const [files, setFiles] = useState([]);
  const [attached, setAttached] = useState([]); // file ids attached to current message
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [voiceStart, setVoiceStart] = useState(false);
  const [preparingCorpus, setPreparingCorpus] = useState(false);
  const [voiceConfig, setVoiceConfig] = useState(null);
  const [vadAggressiveness, setVadAggressiveness] = useState(2);
  const [preview, setPreview] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [transcriptOpen, setTranscriptOpen] = useState(null); // recording id
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [storageOpen, setStorageOpen] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_OPEN_KEY) ?? "false"); }
    catch { return false; }
  });
  // Default: collapsed. Users have asked for the cleanest possible canvas;
  // they can open the rail to access chat history when they need it.
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try { return JSON.parse(localStorage.getItem(SIDEBAR_OPEN_KEY) ?? "false"); }
    catch { return false; }
  });
  const [pendingInput, setPendingInput] = useState("");
  const abortRef = useRef(null);
  const filesPollRef = useRef(null);
  const recordingsPollRef = useRef(null);
  const storageFileInputRef = useRef(null);
  const chatInputRef = useRef(null);
  // Problem 2 — guard against the chat-panel "wipe and reload" race when a
  // brand-new conversation is created in the background for an auto-inspect.
  // We track the freshly-created convId here so loadMessages() can skip its
  // setMessages([]) / setAttached([]) reset for that one conversation while
  // the inspect SSE is still streaming.
  const freshConvRef = useRef(null);
  // Tracks the previous convId so we can detect real conv→conv switches
  // (vs the undefined→newId handoff inside ensureConversation()).
  const prevConvIdRef = useRef(null);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_OPEN_KEY, JSON.stringify(storageOpen)); } catch {}
  }, [storageOpen]);
  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_OPEN_KEY, JSON.stringify(sidebarOpen)); } catch {}
  }, [sidebarOpen]);

  const activeConv = conversations.find((c) => c.id === convId);

  const loadConversations = useCallback(async () => {
    try {
      const { data } = await api.get("/conversations");
      setConversations(data);
    } catch {}
  }, []);

  const loadModels = useCallback(async () => {
    try {
      const { data } = await api.get("/models");
      setModels(data);
      if (data.length) {
        const def = data.find((m) => m.is_default) || data[0];
        setSelectedModelId((cur) => cur || def.id);
      }
    } catch {}
  }, []);

  const loadFiles = useCallback(async () => {
    try {
      const { data } = await api.get("/files");
      setFiles(data);
    } catch {}
  }, []);

  const loadRecordings = useCallback(async () => {
    try {
      const { data } = await api.get("/voice/recordings");
      setRecordings(data);
    } catch {}
  }, []);

  const loadMessages = useCallback(async (id) => {
    if (!id) {
      setMessages([]);
      setAttached([]);
      return;
    }
    try {
      const { data } = await api.get(`/conversations/${id}/messages`);
      // Bullet-proof in-flight bubble preservation. Any client-side message
      // whose id still starts with "inspect-" hasn't been persisted yet
      // (either still streaming, or showing a skip notice). We always
      // merge those on top of the server payload — fixes the first-attach
      // race where a fast SSE skip completes BEFORE loadMessages runs.
      setMessages((prev) => {
        const inflight = prev.filter(
          (m) => typeof m.id === "string" && m.id.startsWith("inspect-"),
        );
        return [...data, ...inflight];
      });
      // Block 5 — Option A: restore chips from the most recent message
      // that had file_ids attached, so the user can keep iterating on the
      // same files without re-clicking "Adjuntar". But: never wipe an
      // attached set the user is currently building for a brand-new conv.
      if (data.length > 0) {
        const lastWithFiles = [...data].reverse().find((m) => (m.file_ids || []).length > 0);
        setAttached(lastWithFiles ? [...lastWithFiles.file_ids] : []);
      }
    } catch {
      setMessages((prev) => prev.filter((m) => typeof m.id === "string" && m.id.startsWith("inspect-")));
    }
  }, []);

  useEffect(() => {
    loadConversations();
    loadModels();
    loadFiles();
    loadRecordings();
    api.get("/voice-settings").then(({ data }) => {
      if (data?.vad_aggressiveness) setVadAggressiveness(data.vad_aggressiveness);
    }).catch(() => {});
  }, [loadConversations, loadModels, loadFiles, loadRecordings]);

  useEffect(() => {
    // When navigating between two existing conversations, drop any in-flight
    // inspect bubbles so they don't "follow" the user to a different chat.
    // We only do this when prev convId was a real id (not undefined → new id,
    // which is the ensureConversation() handoff that must keep the bubble).
    if (prevConvIdRef.current && prevConvIdRef.current !== convId) {
      setMessages((prev) => prev.filter(
        (m) => !(typeof m.id === "string" && m.id.startsWith("inspect-")),
      ));
    }
    prevConvIdRef.current = convId;
    loadMessages(convId);
    setStreamText("");
  }, [convId, loadMessages]);

  // Poll files while any are processing — use a stable polling loop that
  // doesn't restart on every files-state update (avoids losing the "ready"
  // transition when interval timer keeps getting reset).
  useEffect(() => {
    const ACTIVE = new Set([
      "uploading", "queued", "downloading_media", "extracting_meta",
      "converting", "vlm_processing", "transcribing", "chunking", "embedding",
      "processing",
    ]);
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      await loadFiles();
    };
    const interval = setInterval(() => {
      // Always poll, but only if at least one file is in an active state
      // OR has a summary that's still generating.
      const snap = filesPollRef.current?.snapshot || [];
      const hasActive = snap.some(
        (f) => ACTIVE.has(f.status) || f.summary_status === "generating",
      );
      if (hasActive) tick();
    }, 3000);
    // also refresh on tab focus (recovers from browser throttling)
    const onFocus = () => loadFiles();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [loadFiles]);

  // Mirror files into a ref so the stable polling loop can read latest snapshot
  useEffect(() => {
    if (!filesPollRef.current) filesPollRef.current = {};
    filesPollRef.current.snapshot = files;
  }, [files]);

  // Poll voice recordings while any are still uploading/transcribing.
  useEffect(() => {
    const ACTIVE = new Set(["uploading", "transcribing"]);
    if (!recordingsPollRef.current) recordingsPollRef.current = {};
    recordingsPollRef.current.snapshot = recordings;
    const interval = setInterval(() => {
      const hasActive = (recordingsPollRef.current?.snapshot || []).some((r) => ACTIVE.has(r.status));
      if (hasActive) loadRecordings();
    }, 4000);
    return () => clearInterval(interval);
  }, [recordings, loadRecordings]);

  const newChat = () => navigate("/workspace");

  const deleteConv = async (id) => {
    if (!window.confirm(t("workspace:confirm_delete", "¿Borrar esta conversación?"))) return;
    await api.delete(`/conversations/${id}`);
    if (id === convId) navigate("/workspace");
    loadConversations();
  };

  const handleSend = async (text) => {
    if (!selectedModelId) return toast.error(t("workspace:no_model", "Sin modelo disponible — pedile al admin que configure los modelos."));
    if (!text.trim()) return;

    setStreaming(true);
    setStreamText("");
    const tempUserMsg = {
      id: "tmp-" + Date.now(),
      role: "user",
      content: text,
      file_ids: attached,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, tempUserMsg]);

    const body = {
      conversation_id: convId || null,
      model_alias_id: selectedModelId,
      user_message: text,
      file_ids: attached,
    };

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const token = localStorage.getItem("nexa_token");
      const resp = await fetch(`${API_BASE}/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify(body),
        signal: ctrl.signal,
      });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(t || "Stream failed");
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let newConvId = convId;
      let assembled = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const blocks = buffer.split("\n\n");
        buffer = blocks.pop() || "";
        for (const block of blocks) {
          const lines = block.split("\n");
          let ev = "message";
          let dataLine = "";
          for (const ln of lines) {
            if (ln.startsWith("event:")) ev = ln.slice(6).trim();
            else if (ln.startsWith("data:")) dataLine += ln.slice(5).trim();
          }
          if (!dataLine) continue;
          let parsed;
          try { parsed = JSON.parse(dataLine); } catch { continue; }
          if (ev === "meta" && parsed.conversation_id) {
            newConvId = parsed.conversation_id;
          } else if (ev === "token") {
            assembled += parsed.t || "";
            setStreamText(assembled);
          } else if (ev === "error") {
            toast.error(parsed.message || "Streaming error");
          } else if (ev === "done") {
            if (parsed.conversation_id) newConvId = parsed.conversation_id;
          }
        }
      }
      // refresh
      if (newConvId && newConvId !== convId) {
        navigate(`/workspace/${newConvId}`);
      } else {
        await loadMessages(newConvId);
      }
      await loadConversations();
      // Bug 2 — surface the LLM-generated title (fired async on the backend
      // after the assistant reply completes; takes ~2-5 s) in the sidebar
      // with a small adaptive backoff to handle slow LLM responses.
      [3000, 8000].forEach((ms) => setTimeout(() => loadConversations(), ms));
      setAttached([]);
      setStreamText("");
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to send");
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  /**
   * Voice-3 — clicking the Call button next to Send: ensure a conversation
   * exists (auto-create like file attach does), pre-index the conversation
   * corpus into Tensornex/Qdrant so the AI has retrieval context for the
   * call, then open the mode-picker modal.
   */
  const handleStartVoiceCall = async () => {
    const targetConv = await ensureConversation();
    if (!targetConv) {
      toast.error(t("workspace:voice.no_conv", "Could not start a conversation"));
      return;
    }
    setPreparingCorpus(true);
    try {
      const { data } = await api.post("/voice/prepare-corpus", {
        conversation_id: targetConv,
      });
      if (data?.ok === false && data?.skipped !== "empty_conversation") {
        toast.message(
          t("workspace:voice.corpus_degraded",
            "Voice call will start with limited context."),
        );
      }
    } catch (err) {
      console.warn("prepare-corpus failed", err);
    } finally {
      setPreparingCorpus(false);
    }
    setVoiceStart(true);
  };

  /**
   * If the user uploads/attaches a file while on the empty `/workspace`
   * route, eagerly create a fresh conversation so the auto-inspect bubble
   * has somewhere to live. Returns the conversation id (existing or new).
   */
  const ensureConversation = async () => {
    if (convId) return convId;
    try {
      const { data } = await api.post("/conversations", { title: null });
      // Problem 2 — flag this convId so the loadMessages() race won't wipe
      // the in-flight inspect placeholder while navigation is settling.
      freshConvRef.current = data.id;
      await loadConversations();
      navigate(`/workspace/${data.id}`, { replace: true });
      return data.id;
    } catch {
      return null;
    }
  };

  /**
   * Block 4 — Auto-inspect Option D (polling). Kicks off the inspect job
   * via POST, then polls GET /status every ~2 s until the job completes,
   * errors, or skips. Bullet-proof against ingress buffering and survives
   * browser refresh because every poll is a complete request/response.
   */
  const maybeInspect = async (targetConvId, fileId) => {
    if (!targetConvId || !fileId) return;
    const inspectId = "inspect-" + fileId + "-" + Date.now();
    let placeholderInjected = false;
    const ensurePlaceholder = (initial) => {
      if (placeholderInjected) return;
      placeholderInjected = true;
      setMessages((m) => [
        ...m,
        {
          id: inspectId,
          role: "assistant",
          content: "",
          file_ids: [fileId],
          meta: { type: "auto_inspect", phases: [], filename: null, streaming: true, ...(initial || {}) },
          created_at: new Date().toISOString(),
        },
      ]);
    };
    const updateMeta = (mut) =>
      setMessages((m) =>
        m.map((x) => (x.id === inspectId ? { ...x, meta: { ...x.meta, ...mut(x.meta) } } : x)),
      );
    const updateContent = (c) =>
      setMessages((m) => m.map((x) => (x.id === inspectId ? { ...x, content: c } : x)));
    const removePlaceholder = () =>
      setMessages((m) => m.filter((x) => x.id !== inspectId));
    const swapId = (newId) =>
      setMessages((m) =>
        m.map((x) =>
          x.id === inspectId ? { ...x, id: newId, meta: { ...x.meta, streaming: false } } : x,
        ),
      );

    const refreshConvs = () => {
      loadConversations();
      // Auto-title runs in a fire-and-forget asyncio task (~2-5s); hedge.
      [3000, 8000].forEach((ms) => setTimeout(() => loadConversations(), ms));
    };

    try {
      // ----- Kick off the job ---------------------------------------------
      const { data: start } = await api.post(
        `/conversations/${targetConvId}/auto-inspect?file_id=${encodeURIComponent(fileId)}`,
      );

      // Synchronous outcomes — handled without any polling.
      if (start.status === "skipped") {
        if (start.reason === "disabled") return;
        ensurePlaceholder();
        updateMeta(() => ({
          streaming: false,
          skipped: true,
          skip_reason: start.reason,
          skip_message: start.message || "",
        }));
        return;
      }
      if (start.status === "cached") {
        // Same-conv replay — the server already has the message; just refresh.
        if (placeholderInjected) removePlaceholder();
        await loadMessages(targetConvId);
        return;
      }
      if (start.status === "cached_from_other_conv") {
        ensurePlaceholder({
          cached_label: start.label || "",
          cached_from: start.cached_from || null,
        });
        updateContent(start.message?.content || "");
        if (start.message?.id) swapId(start.message.id);
        refreshConvs();
        return;
      }
      if (start.status !== "running" || !start.job_id) {
        if (placeholderInjected) removePlaceholder();
        return;
      }

      ensurePlaceholder({ filename: start.filename || null });
      const jobId = start.job_id;

      // ----- Poll until completed / error / skipped -----------------------
      let consecutiveErrors = 0;
      while (true) {
        await new Promise((r) => setTimeout(r, 2000));
        let s;
        try {
          const { data } = await api.get(
            `/conversations/${targetConvId}/auto-inspect/${jobId}/status`,
          );
          s = data;
          consecutiveErrors = 0;
        } catch (err) {
          consecutiveErrors += 1;
          if (consecutiveErrors >= 5) {
            updateMeta(() => ({ error: "Lost connection", streaming: false }));
            return;
          }
          continue;
        }

        // Surface live phase progress in the bubble (deduped by stage).
        if (s.phase) {
          updateMeta((cur) => ({
            phases: [
              ...(cur.phases || []),
              {
                stage: s.phase,
                sub_status: s.sub_status,
                progress_pct: s.progress_pct,
                waited_seconds: s.waited_seconds,
              },
            ],
          }));
        }

        if (s.status === "completed") {
          updateContent(s.content || "");
          if (s.message_id) swapId(s.message_id);
          else updateMeta(() => ({ streaming: false }));
          refreshConvs();
          return;
        }
        if (s.status === "error") {
          updateMeta(() => ({ error: s.error || "Inspect failed", streaming: false }));
          return;
        }
        if (s.status === "skipped") {
          updateMeta(() => ({
            streaming: false,
            skipped: true,
            skip_reason: s.skip_reason,
            skip_message: s.skip_message || "",
          }));
          return;
        }
        // status === "queued" | "polling" — keep polling.
      }
    } catch (err) {
      console.error("maybeInspect failed", err);
      if (placeholderInjected) removePlaceholder();
    } finally {
      if (freshConvRef.current === targetConvId) {
        freshConvRef.current = null;
      }
    }
  };

  const handleFileUpload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("workspace:file_uploading", { name: file.name, defaultValue: "{{name}} subiendo…" }));
      setFiles((f) => [data, ...f]);
      const targetConv = await ensureConversation();
      setAttached((a) => [...a, data.id]);
      // Block 4 — fire auto-inspect when we have a conversation context.
      if (targetConv) maybeInspect(targetConv, data.id);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "unsupported_file_type") {
        toast.error(t("workspace:upload_unsupported", {
          ext: detail.ext || "?",
          defaultValue: "Tipo de archivo no soportado: .{{ext}}",
        }));
      } else {
        toast.error(t("workspace:upload_failed", "Subida fallida"));
      }
    }
  };

  const handleUrlAdd = async (url) => {
    try {
      const { data } = await api.post("/files/url", { url });
      toast.success(t("workspace:url_queued", "URL en cola para extracción"));
      setFiles((f) => [data, ...f]);
      const targetConv = await ensureConversation();
      setAttached((a) => [...a, data.id]);
      if (targetConv) maybeInspect(targetConv, data.id);
    } catch {
      toast.error(t("workspace:url_failed", "No pudimos agregar la URL"));
    }
  };

  const handleDeleteFile = async (id) => {
    await api.delete(`/files/${id}`);
    setFiles((f) => f.filter((x) => x.id !== id));
    setAttached((a) => a.filter((x) => x !== id));
  };

  const handleRetryFile = async (id) => {
    try {
      await api.post(`/files/${id}/retry`);
      toast.success(t("workspace:retry_started", "Reintentando extracción…"));
      loadFiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("workspace:retry_failed", "Reintento fallido"));
    }
  };

  const handlePreviewPrompt = async () => {
    try {
      const { data } = await api.post("/prompts/preview", {
        file_ids: attached,
        mode: "chat",
      });
      setPreview(data);
    } catch (err) {
      toast.error(t("workspace:prompt_failed", "No pudimos cargar el prompt"));
    }
  };

  const toggleAttach = async (id) => {
    const alreadyAttached = attached.includes(id);
    setAttached((a) => (alreadyAttached ? a.filter((x) => x !== id) : [...a, id]));
    if (alreadyAttached) return; // just removing — no inspect
    const targetConv = await ensureConversation();
    if (targetConv) maybeInspect(targetConv, id);
  };

  // -------------------------------------------------------------------------
  // Per-message actions (W7): regenerate last AI message + fork from any
  // -------------------------------------------------------------------------
  const handleRegenerate = async () => {
    if (streaming) return;
    // Find last user message; resend it.
    const lastUser = [...messages].reverse().find((m) => m.role === "user");
    if (!lastUser) return toast.error(t("workspace:nothing_to_regen", "No hay nada que regenerar."));
    // Optimistically drop the last assistant message so the UI shows the
    // fresh stream replacing it (backend just appends a new one).
    setMessages((m) => {
      const out = [...m];
      while (out.length && out[out.length - 1].role !== "user") out.pop();
      return out;
    });
    await handleSend(lastUser.content);
  };

  const handleFork = async (message) => {
    try {
      const { data } = await api.post(`/conversations/${convId}/fork`, {
        up_to_message_id: message.id,
      });
      toast.success(t("workspace:forked", "Bifurcado en un nuevo chat"));
      await loadConversations();
      navigate(`/workspace/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("workspace:fork_failed", "Bifurcación fallida"));
    }
  };

  // -------------------------------------------------------------------------
  // ⌘K / Ctrl-K command palette shortcut
  // -------------------------------------------------------------------------
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const triggerStorageUpload = () => storageFileInputRef.current?.click();
  const onPickSample = (text) => {
    setPendingInput(text);
    // focus the input after state propagates
    setTimeout(() => chatInputRef.current?.focus?.(), 50);
  };

  return (
    <div className="h-screen w-screen flex flex-col bg-background">
      {/* Top bar */}
      <header className="border-b border-border h-14 flex items-center justify-between px-4 lg:px-6 bg-background z-30">
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2" data-testid="workspace-logo">
            <BrandLogo size="md" />
          </Link>
          <span className="text-xs font-mono text-muted-foreground hidden sm:inline">/ {t("nav.workspace")}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden md:flex items-center gap-2 mr-2">
            <span className="text-xs uppercase tracking-wider font-mono text-muted-foreground">{t("workspace:model", "Modelo")}</span>
            <Select value={selectedModelId || ""} onValueChange={setSelectedModelId}>
              <SelectTrigger className="w-[200px] h-9 rounded-sm" data-testid="model-selector">
                <SelectValue placeholder={t("workspace:no_models", "Sin modelos")} />
              </SelectTrigger>
              <SelectContent>
                {models.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">{t("workspace:no_models_yet", "Sin modelos todavía")}</div>}
                {models.map((m) => (
                  <SelectItem key={m.id} value={m.id} data-testid={`model-option-${m.id}`}>
                    <div className="flex flex-col">
                      <span className="font-medium">{m.alias_name}</span>
                      {m.description && (
                        <span className="text-xs font-mono text-muted-foreground">{m.description}</span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handlePreviewPrompt}
            className="rounded-sm"
            data-testid="preview-prompt-button"
            title={t("workspace:prompt_tooltip", "Ver el prompt del sistema que se enviará al modelo")}
          >
            <Eye className="h-4 w-4 mr-1.5" /> {t("workspace:prompt", "Prompt")}
          </Button>
          {user?.role === "super_admin" && (
            <Link to="/admin" data-testid="goto-admin">
              <Button variant="ghost" size="sm" className="rounded-sm">
                <Shield className="h-4 w-4 mr-1.5" /> {t("nav.admin")}
              </Button>
            </Link>
          )}
          <UserMenu variant="workspace" />
        </div>
      </header>

      {/* Body: 3-pane with collapsible storage */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left chat-list rail — collapsible to a thin 36 px stub for a
            cleaner default canvas. Mirrors the right storage rail's pattern. */}
        <aside
          className={`shrink-0 border-r border-border flex flex-col overflow-hidden bg-secondary/30 hidden md:flex transition-[width] duration-200 ${
            sidebarOpen ? "w-64 lg:w-72" : "w-10"
          }`}
          data-testid="left-sidebar"
        >
          {!sidebarOpen ? (
            <div className="flex flex-col items-center py-2 gap-2 h-full">
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-1.5 rounded-sm hover:bg-secondary transition text-muted-foreground hover:text-foreground"
                data-testid="sidebar-expand-button"
                title={t("workspace:sidebar.expand", "Expandir conversaciones")}
              >
                <PanelLeftOpen className="h-4 w-4" />
              </button>
              <button
                onClick={newChat}
                className="p-1.5 rounded-sm hover:bg-secondary transition text-muted-foreground hover:text-foreground"
                data-testid="sidebar-new-chat-collapsed"
                title={t("workspace:sidebar.new_chat")}
              >
                <Plus className="h-4 w-4" />
              </button>
              <button
                onClick={() => setPaletteOpen(true)}
                className="p-1.5 rounded-sm hover:bg-secondary transition text-muted-foreground hover:text-foreground"
                data-testid="sidebar-palette-collapsed"
                title={t("workspace:palette.title", "Paleta de comandos (⌘K)")}
              >
                <Command className="h-4 w-4" />
              </button>
              <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-muted-foreground mt-1" style={{ writingMode: "vertical-rl" }}>
                {t("workspace:sidebar.rail", "CHATS")} · {conversations.length}
              </div>
            </div>
          ) : (
            <>
              <div className="p-3 border-b border-border flex gap-2">
                <Button onClick={newChat} className="flex-1 h-10 rounded-sm" data-testid="new-chat-button">
                  <Plus className="h-4 w-4 mr-1.5" /> {t("workspace:sidebar.new_chat")}
                </Button>
                <Button
                  variant="outline"
                  className="h-10 px-2 rounded-sm"
                  onClick={() => setPaletteOpen(true)}
                  data-testid="open-command-palette-button"
                  title={t("workspace:palette.title", "Paleta de comandos (⌘K)")}
                >
                  <Command className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  className="h-10 px-2 rounded-sm"
                  onClick={() => setSidebarOpen(false)}
                  data-testid="sidebar-collapse-button"
                  title={t("workspace:sidebar.collapse", "Colapsar")}
                >
                  <PanelLeftClose className="h-4 w-4" />
                </Button>
              </div>
              <Sidebar
                conversations={conversations}
                activeId={convId}
                onDelete={deleteConv}
                recordings={recordings}
                onOpenTranscript={(rid) => setTranscriptOpen(rid)}
              />
            </>
          )}
        </aside>

        <main className="flex-1 flex flex-col overflow-hidden border-r border-border min-w-0">
          <div className="px-4 pt-3">
            <StrandedEmbeddingBanner />
          </div>
          {messages.length === 0 && !streaming ? (
            <div className="flex-1 overflow-y-auto">
              <WorkspaceEmptyState user={user} />
            </div>
          ) : (
            <ChatThread
              messages={messages}
              streaming={streaming}
              streamText={streamText}
              user={user}
              modelName={models.find((m) => m.id === selectedModelId)?.alias_name}
              onRegenerate={handleRegenerate}
              onFork={convId ? handleFork : undefined}
              conversationId={convId}
              fileIds={attached || []}
            />
          )}
          <ChatInput
            ref={chatInputRef}
            onSend={handleSend}
            onUpload={handleFileUpload}
            onUrl={handleUrlAdd}
            attached={attached}
            files={files}
            onUnattach={(id) => toggleAttach(id)}
            disabled={streaming || !selectedModelId}
            injectedText={pendingInput}
            onStartVoice={handleStartVoiceCall}
            preparingCorpus={preparingCorpus}
          />
        </main>

        <aside
          className={`hidden md:flex flex-col overflow-hidden bg-secondary/30 transition-all duration-200 ${
            storageOpen ? "w-72 lg:w-80 shrink-0" : "w-10 shrink-0"
          }`}
        >
          <StoragePanel
            files={files}
            attached={attached}
            onToggleAttach={toggleAttach}
            onDelete={handleDeleteFile}
            onUpload={handleFileUpload}
            onUrl={handleUrlAdd}
            onRetry={handleRetryFile}
            onRefreshFiles={loadFiles}
            open={storageOpen}
            onToggleOpen={() => setStorageOpen((o) => !o)}
          />
        </aside>
        {/* Hidden file input for the empty-state "upload" CTA */}
        <input
          type="file"
          ref={storageFileInputRef}
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFileUpload(f);
            e.target.value = "";
          }}
        />
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-md grid place-items-center p-4" data-testid="prompt-preview-modal">
          <div className="w-full max-w-3xl border border-border bg-card max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] font-mono text-muted-foreground">[ {t("workspace:prompt_preview.eyebrow", "prompt compilado")} ]</div>
                <div className="font-display font-bold tracking-tight text-lg mt-0.5">{t("workspace:prompt_preview.title", "Lo que va a recibir el modelo")}</div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setPreview(null)} data-testid="prompt-preview-close">
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="overflow-auto p-5">
              <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-1.5">{t("workspace:prompt_preview.system", "Prompt del sistema (chat)")}</div>
              <pre className="text-sm font-mono whitespace-pre-wrap bg-secondary/50 border border-border p-4 rounded-sm">{preview.compiled}</pre>
              <div className="text-[11px] font-mono text-muted-foreground mt-3">
                {t("workspace:prompt_preview.hint", "Editá el template en Admin → Prompts para cambiar esto para todos.")}
              </div>
            </div>
          </div>
        </div>
      )}

      {voiceStart && (
        <VoiceStartModal
          selectedModelName={models.find((m) => m.id === selectedModelId)?.alias_name}
          hasModel={!!selectedModelId}
          onClose={() => setVoiceStart(false)}
          onStart={(cfg) => {
            setVoiceConfig(cfg);
            setVoiceStart(false);
            setVoiceOpen(true);
          }}
        />
      )}

      {voiceOpen && voiceConfig && (
        <VoiceCallModal
          mode={voiceConfig.mode}
          language={voiceConfig.language}
          record={voiceConfig.record}
          conversationId={convId}
          attachedFileIds={attached}
          selectedModelId={selectedModelId}
          selectedModelName={models.find((m) => m.id === selectedModelId)?.alias_name}
          vadAggressiveness={vadAggressiveness}
          onConversationCreated={(newConvId) => {
            if (newConvId && newConvId !== convId) {
              navigate(`/workspace/${newConvId}`, { replace: true });
            }
          }}
          onClose={() => {
            setVoiceOpen(false);
            setVoiceConfig(null);
            loadConversations();
            loadRecordings();
            // Issue 4 — auto-title fires async after voice transcript save;
            // give the LLM ~3s + ~8s to land the title in DB before refreshing.
            [3000, 8000].forEach((ms) => setTimeout(() => loadConversations(), ms));
            if (convId) loadMessages(convId);
          }}
        />
      )}

      {transcriptOpen && (
        <VoiceTranscriptModal
          recordingId={transcriptOpen}
          onClose={() => setTranscriptOpen(null)}
          onDeleted={() => { loadRecordings(); loadConversations(); }}
        />
      )}

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        conversations={conversations}
        files={files}
        models={models}
        selectedModelId={selectedModelId}
        isAdmin={user?.role === "super_admin"}
        onNewChat={newChat}
        onOpenVoice={handleStartVoiceCall}
        onPreviewPrompt={handlePreviewPrompt}
        onOpenAdmin={() => navigate("/admin")}
        onSelectModel={(id) => { setSelectedModelId(id); toast.success(t("workspace:model_switched", "Modelo cambiado")); }}
        onAttachFile={(id) => {
          if (!attached.includes(id)) {
            setAttached((a) => [...a, id]);
            toast.success(t("workspace:attached", "Adjuntado"));
          }
        }}
        onUploadFile={triggerStorageUpload}
        onJumpConversation={(id) => navigate(`/workspace/${id}`)}
      />
    </div>
  );
}
